import { useState, useEffect, useMemo, useRef } from 'react'
import SkillCard from '../components/SkillCard'
import NewSkillCard from '../components/NewSkillCard'
import ProposedSkillDialog from '../components/ProposedSkillDialog'
import SkillDetailsModal from '../components/SkillDetailsModal'
import AddSkillModal from '../components/AddSkillModal'
import MergeSkillModal from '../components/MergeSkillModal'
import DuplicateSkillModal from '../components/DuplicateSkillModal'
import { getTokenPayload } from '../api/security'
import { useIsMobile } from '../hooks/useIsMobile'
import styles from './Skills.module.css'

// The right sidebar (Skill Proposals — proposed/candidate/rejected review) is
// gated on org_skill_management specifically. admin_right grants no skill
// rights on its own — an admin without this right sees the same left-only
// layout as anyone else without it. superadmin_right overrides this (and
// every other granular right) tenant-wide.
function canReviewOrgSkills() {
  const rights = getTokenPayload()?.rights
  return Boolean(rights?.org_skill_management) || Boolean(rights?.superadmin_right)
}

import {
  fetchSkillsCoverage, fetchSkillCoverage, fetchSkillCandidates, submitSkillDecision,
  fetchSkillTypes, createSkill,
} from '../api/skills'

// Coverage-status hierarchy for the "Status" sort: Strong is highest, then
// Adequate, Neutral, Insufficient (per the design doc — not alpha-numeric).
const STATUS_RANK = { Strong: 4, Adequate: 3, Neutral: 2, Insufficient: 1 }

// Sort options for the skill-card list. "type" and "coverage type" in the design
// doc map to the single skill `type` field, so they're one option here.
const SORT_OPTIONS = [
  { key: 'name',    label: 'Name' },
  { key: 'type',    label: 'Type' },
  { key: 'percent', label: 'Coverage %' },
  { key: 'status',  label: 'Status' },
]

const CANDIDATE_FILTERS = [
  { status: 'proposed', label: 'Proposed' },
  { status: 'normalized', label: 'Candidates' },
  { status: 'rejected', label: 'Rejected' },
]
const REVIEWABLE_CANDIDATE_STATUSES = new Set(CANDIDATE_FILTERS.map(filter => filter.status))

// Persist the coverage-list sort across navigation (the page unmounts on route
// change, so component state alone is lost). localStorage keeps the choice between
// sessions too. Reads are validated so a stale/garbage value falls back to default.
const SORT_KEY_STORAGE = 'skills.sortKey'
const SORT_DIR_STORAGE = 'skills.sortDir'
const VALID_SORT_KEYS = SORT_OPTIONS.map(o => o.key)

function readStoredSortKey() {
  try {
    const v = localStorage.getItem(SORT_KEY_STORAGE)
    return VALID_SORT_KEYS.includes(v) ? v : 'name'
  } catch { return 'name' }
}

function readStoredSortDir() {
  try {
    const v = localStorage.getItem(SORT_DIR_STORAGE)
    return v === 'asc' || v === 'desc' ? v : 'asc'
  } catch { return 'asc' }
}

// percent is calculated in the UI: agent_count / target_agent_count.
function coveragePercent(skill) {
  const target = skill.target_agent_count
  if (!target) return 0
  return Math.round((skill.agent_count / target) * 100)
}

// ---------------------------------------------------------------------------
// Skills page
// ---------------------------------------------------------------------------
export default function Skills() {
  const canReviewOrg = canReviewOrgSkills()
  const isMobile = useIsMobile()
  // Mobile only: which of the two panels is showing (both render side by
  // side on desktop, so this is ignored there). Irrelevant when there's no
  // proposals panel at all (!canReviewOrg) — coverage is the only content.
  const [mobileTab, setMobileTab] = useState('coverage')
  const [skills,    setSkills]    = useState([])
  const [toApprove, setToApprove] = useState([])
  const [loading,   setLoading]   = useState({ coverage: true, approve: true })
  const [error,     setError]     = useState(null)

  const [search,  setSearch]  = useState('')
  const [sortKey, setSortKey] = useState(readStoredSortKey)
  const [sortDir, setSortDir] = useState(readStoredSortDir)
  const [candidateStatus, setCandidateStatus] = useState('proposed')

  // Mirror the sort choice to localStorage so it survives navigating away and back.
  useEffect(() => {
    try { localStorage.setItem(SORT_KEY_STORAGE, sortKey) } catch { /* storage unavailable */ }
  }, [sortKey])
  useEffect(() => {
    try { localStorage.setItem(SORT_DIR_STORAGE, sortDir) } catch { /* storage unavailable */ }
  }, [sortDir])

  // Pending decision { mode: 'approve'|'reject', skill } or null — drives the
  // ProposedSkillDialog (skill-type picker on approve, reason on reject). `busy`
  // disables the dialog's confirm while the call is in flight.
  const [decision, setDecision] = useState(null)
  const [busy, setBusy] = useState(false)

  // The active skill whose Details modal is open, or null. Clicking a coverage
  // card sets it; the modal is keyed on the skill so it remounts (refetches) per skill.
  const [detailSkill, setDetailSkill] = useState(null)

  // Add Skill Manually modal: open flag, the skill-types list (fetched lazily on
  // first open and cached), an inflight guard, and an in-dialog error.
  const [addOpen,   setAddOpen]   = useState(false)
  const [skillTypes, setSkillTypes] = useState([])
  const [addBusy,   setAddBusy]   = useState(false)
  const [addError,  setAddError]  = useState('')
  const [mergeSkill, setMergeSkill] = useState(null)
  const [mergeBusy, setMergeBusy] = useState(false)
  const [mergeError, setMergeError] = useState('')

  // Set when approving a proposed skill is refused because an active skill already has
  // that name: { candidate, existing } drives the DuplicateSkillModal (merge instead).
  const [duplicate, setDuplicate] = useState(null)
  const [duplicateBusy, setDuplicateBusy] = useState(false)
  const [duplicateError, setDuplicateError] = useState('')

  // Ref guards suppress React 18 StrictMode's dev double-invoke (mirrors Team).
  const didFetchCoverage = useRef(false)
  useEffect(() => {
    if (didFetchCoverage.current) return
    didFetchCoverage.current = true
    fetchSkillsCoverage()
      .then(data => setSkills(data.skills ?? []))
      .catch(e => setError({ message: e.message }))
      .finally(() => setLoading(prev => ({ ...prev, coverage: false })))
  }, [])

  // The Details modal calls this once on close, but only if it changed the skill
  // (a member removal, etc.). We re-pull just that one skill in coverage shape and
  // merge it by id — agent_count and the backend-computed coverage_status are owned
  // by the server (thresholds are server-side, soon tenant-configurable), so we
  // refetch rather than recompute in the UI. A missing skill is left as-is.
  function refreshSkill(skillId) {
    fetchSkillCoverage(skillId)
      .then(fresh => setSkills(list => list.map(s => s.id === fresh.id ? fresh : s)))
      .catch(e => setError({ message: e.message }))
  }

  // Open the Details modal for a coverage card. The skill types the modal's Edit dialog
  // needs are loaded best-effort in the background (cached thereafter) — a failure here
  // never blocks the modal; only the Edit type picker would be empty.
  function openDetails(skill) {
    setDetailSkill(skill)
    if (skillTypes.length === 0) {
      fetchSkillTypes().then(d => setSkillTypes(d.types ?? [])).catch(() => {})
    }
  }

  // The Details modal calls this right after it renames/reclassifies a skill, so the
  // coverage card (and the page's record of the open skill) reflect the change at once
  // without a refetch — name and type are exactly what we sent.
  function applySkillUpdate(skillId, patch) {
    setSkills(list => list.map(s => s.id === skillId ? { ...s, ...patch } : s))
    setDetailSkill(d => (d && d.id === skillId ? { ...d, ...patch } : d))
    // coverage_status and the recommendation text are computed server-side from
    // agent_count / target_agent_count, so patching the target alone leaves them stale
    // (a skill that just became 100% filled would still read Insufficient). Pull the
    // fresh coverage row for just this skill.
    refreshSkill(skillId)
  }

  // Open the Approve dialog for any reviewable candidate. The type picker is front-and-center,
  // so — like openAddSkill — load the skill types (cached thereafter) BEFORE opening, so
  // the list is present at the dialog's mount and its default type resolves correctly.
  async function openApprove(skill) {
    // Same-named live skill? Warn right away, before the type picker — there's nothing to
    // classify when the only sensible outcome is a merge. This checks the coverage list the
    // page already holds (instant, no request); the server re-checks on approve, so a skill
    // added since this list loaded still can't be duplicated (see handleDecision's 409).
    const wanted = skill.skill_name.trim().toLowerCase()
    const existing = skills.find(s => s.name.trim().toLowerCase() === wanted)
    if (existing) {
      setDuplicateError('')
      setDuplicate({
        candidate: skill,
        existing: { id: existing.id, name: existing.name, type: existing.type, agent_count: existing.agent_count },
      })
      return
    }
    if (skillTypes.length === 0) {
      try {
        const data = await fetchSkillTypes()
        setSkillTypes(data.types ?? [])
      } catch (e) {
        setError({ message: e.detail || e.message })
        return
      }
    }
    setDecision({ mode: 'approve', skill })
  }

  // Re-pull the proposed-candidate list and clear any error. Used on initial mount
  // and by the Reload affordance in the error banner when a decision fails with a
  // 409 (the clicked card was approved/rejected elsewhere — the list is stale).
  async function loadCandidates() {
    setError(null)
    setLoading(prev => ({ ...prev, approve: true }))
    try {
      const data = await fetchSkillCandidates()
      setToApprove((data.skills ?? []).filter(skill =>
        REVIEWABLE_CANDIDATE_STATUSES.has(skill.status ?? 'proposed')))
    } catch (e) {
      setError({ message: e.detail || e.message })
    } finally {
      setLoading(prev => ({ ...prev, approve: false }))
    }
  }

  // No org_skill_management (and not a superadmin): the right sidebar never
  // renders (below), so there's nothing for this list to feed — skip the fetch entirely.
  const didFetchApprove = useRef(false)
  useEffect(() => {
    if (!canReviewOrg) return
    if (didFetchApprove.current) return
    didFetchApprove.current = true
    void loadCandidates()
  }, [canReviewOrg])

  const visibleCandidates = useMemo(
    () => toApprove.filter(skill => (skill.status ?? 'proposed') === candidateStatus),
    [toApprove, candidateStatus],
  )

  // Search filters; sort reorders. Search runs first, so a card excluded by the
  // term can't be reordered into view, and clearing the term restores the full
  // sorted list.
  const visible = useMemo(() => {
    const term = search.trim().toLowerCase()
    const filtered = term === ''
      ? skills
      : skills.filter(s =>
          s.name.toLowerCase().split(/\s+/).some(part => part.startsWith(term)))

    const sign = sortDir === 'desc' ? -1 : 1
    const cmp = {
      name:    (a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
      type:    (a, b) => String(a.type ?? '').localeCompare(String(b.type ?? '')),
      percent: (a, b) => coveragePercent(a) - coveragePercent(b),
      status:  (a, b) => (STATUS_RANK[a.coverage_status] ?? 0) - (STATUS_RANK[b.coverage_status] ?? 0),
    }[sortKey]
    return [...filtered].sort((a, b) => sign * cmp(a, b))
  }, [skills, search, sortKey, sortDir])

  // Confirmed from the dialog. Optimistically drop the proposed card; on a successful
  // approve, the response is the new skill in coverage shape — drop it into the
  // coverage list (deduped; the `visible` memo sorts it into place). Restore the card
  // and surface the error on failure.
  async function handleDecision(skill, action, payload) {
    const prev = toApprove
    setToApprove(list => list.filter(s => s.id !== skill.id))
    setBusy(true)
    try {
      const res = await submitSkillDecision(skill.id, action,
        action === 'approve'
          ? { skillType: payload.skill_type }
          : { reason: payload.reason })
      if (action === 'approve' && res?.id != null) {
        setSkills(list => [res, ...list.filter(s => s.id !== res.id)])
      } else if (action === 'reject') {
        // Re-fetch so the rejected tab immediately receives the server-owned review
        // timestamp and resolved reviewer display name.
        await loadCandidates()
      }
    } catch (e) {
      // Approving would duplicate a live skill: not an error to report — keep the
      // proposal and offer the merge in a modal instead.
      if (action === 'approve' && e.status === 409 && e.detail?.code === 'duplicate_skill') {
        setToApprove(prev)
        setDuplicateError('')
        setDuplicate({ candidate: skill, existing: e.detail.existing_skill })
        return
      }
      // The candidate moved on out from under this view: 409 = it changed state
      // (rejected/mid-pipeline elsewhere), 404 = it's gone (approved/consumed elsewhere).
      // Either way the list is stale, so offer a Reload rather than just restoring the
      // dead card.
      const reloadable = e.status === 409 || e.status === 404
      setError({ message: e.detail || e.message, reloadable })
      setToApprove(prev)
    } finally {
      setBusy(false)
      setDecision(null)
    }
  }

  // Open the Add Skill Manually modal, lazily loading the skill types the dropdown
  // needs on first open (cached thereafter).
  async function openAddSkill() {
    setAddError('')
    if (skillTypes.length === 0) {
      try {
        const data = await fetchSkillTypes()
        setSkillTypes(data.types ?? [])
      } catch (e) {
        setError({ message: e.detail || e.message })
        return
      }
    }
    setAddOpen(true)
  }

  // Save a manually-added skill. On success the backend returns it in coverage shape,
  // so drop it into the list (deduped; the `visible` memo sorts it into place) and
  // close. On failure keep the modal open and show the reason in-dialog.
  async function handleAddSkill({ skillName, skillType }) {
    setAddBusy(true)
    setAddError('')
    try {
      const res = await createSkill({ skillName, skillType })
      if (res?.id != null) {
        setSkills(list => [res, ...list.filter(s => s.id !== res.id)])
      }
      setAddOpen(false)
    } catch (e) {
      setAddError(e.detail || e.message)
    } finally {
      setAddBusy(false)
    }
  }

  // Confirmed in the DuplicateSkillModal: merge the proposal into the same-named skill.
  // Same call as the manual merge. On success drop the card and re-pull the target's
  // coverage row; on failure keep the modal open with the reason.
  async function handleDuplicateMerge() {
    const { candidate, existing } = duplicate
    const prev = toApprove
    setToApprove(list => list.filter(s => s.id !== candidate.id))
    setDuplicateBusy(true)
    setDuplicateError('')
    try {
      await submitSkillDecision(candidate.id, 'merge', { targetSkillId: existing.id })
      setDuplicate(null)
      refreshSkill(existing.id)
    } catch (e) {
      setToApprove(prev)
      setDuplicateError(e.message)
    } finally {
      setDuplicateBusy(false)
    }
  }

  async function handleMerge(targetSkillId) {
    if (!mergeSkill) return
    const candidate = mergeSkill
    const prev = toApprove
    setToApprove(list => list.filter(skill => skill.id !== candidate.id))
    setMergeBusy(true)
    setMergeError('')
    try {
      await submitSkillDecision(candidate.id, 'merge', { targetSkillId })
      setMergeSkill(null)
    } catch (e) {
      setToApprove(prev)
      setMergeError(e.detail || e.message)
    } finally {
      setMergeBusy(false)
    }
  }

  return (
    <div className={styles.page}>

      {error && (
        <div className={styles.errorBanner}>
          <span>{error.message}</span>
          {error.reloadable && (
            <button type="button" className={styles.errorReload} onClick={loadCandidates}>
              Reload
            </button>
          )}
        </div>
      )}

      {/* Mobile only: both panels are full lists, so stacking them (the
          desktop 1024px breakpoint's fallback) buries Proposals below every
          coverage card. A tab switcher shows one panel at a time instead. */}
      {isMobile && canReviewOrg && (
        <div className={styles.mobileTabs} role="tablist" aria-label="Skills view">
          <button
            type="button"
            role="tab"
            className={`${styles.mobileTab} ${mobileTab === 'coverage' ? styles.mobileTabActive : ''}`}
            aria-selected={mobileTab === 'coverage'}
            onClick={() => setMobileTab('coverage')}
          >
            Skill Coverage
          </button>
          <button
            type="button"
            role="tab"
            className={`${styles.mobileTab} ${mobileTab === 'proposals' ? styles.mobileTabActive : ''}`}
            aria-selected={mobileTab === 'proposals'}
            onClick={() => setMobileTab('proposals')}
          >
            Skill Proposals
          </button>
        </div>
      )}

      <div className={`${styles.mainContent} ${!canReviewOrg ? styles.mainContentSingle : ''}`}>

        {/* ── Left — Skills Coverage ─────────────────── */}
        {(!isMobile || !canReviewOrg || mobileTab === 'coverage') && (
        <div className={styles.coveragePanel}>
          <div className={styles.panelHeader}>
            <h2 className={styles.panelTitle}>Skill Coverage</h2>

            <div className={styles.searchWrap}>
              <input
                className={styles.search}
                type="text"
                placeholder="Search for Skill"
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => e.key === 'Escape' && setSearch('')}
              />
              {search && (
                <button type="button" className={styles.clearSearch} onClick={() => setSearch('')}>
                  Clear
                </button>
              )}
            </div>

            <div className={styles.sortControls}>
              <select
                className={styles.sortSelect}
                value={sortKey}
                onChange={e => setSortKey(e.target.value)}
                aria-label="Sort skills by"
              >
                {SORT_OPTIONS.map(o => <option key={o.key} value={o.key}>Sort: {o.label}</option>)}
              </select>
              <button
                type="button"
                className={styles.sortDir}
                onClick={() => setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))}
                title={sortDir === 'asc' ? 'Ascending' : 'Descending'}
                aria-label={sortDir === 'asc' ? 'Ascending' : 'Descending'}
              >
                {sortDir === 'asc' ? '↑' : '↓'}
              </button>
            </div>
          </div>

          <div className={styles.skillList}>
            {loading.coverage
              ? Array(5).fill(0).map((_, i) => <div key={i} className={styles.skillSkeleton} />)
              : visible.length === 0
                ? <div className={styles.emptyState}>No skills match your search.</div>
                : visible.map(skill => (
                    <SkillCard
                      key={skill.id}
                      skillName={skill.name}
                      skillClass={skill.type}
                      skillVariant={String(skill.type ?? '').toLowerCase()}
                      description={skill.description}
                      current={skill.agent_count}
                      target={skill.target_agent_count}
                      percent={coveragePercent(skill)}
                      status={skill.coverage_status}
                      recommendation={skill.recommendation}
                      onSkillClick={() => openDetails(skill)}
                    />
                  ))
            }
          </div>
        </div>
        )}

        {/* ── Right — Proposed Skills ────────────────── */}
        {/* Requires org_skill_management specifically (or superadmin_right) —
            admin_right alone grants no skill rights, so an admin without this
            right never sees this panel either. Not shown at all (not disabled)
            when absent; the coverage panel takes the full width instead
            (.mainContentSingle). Mobile: also gated on mobileTab (tab bar above). */}
        {canReviewOrg && (!isMobile || mobileTab === 'proposals') && (
          <div className={styles.approvePanel}>
            {/* Same .panelHeader as the coverage panel so its fixed height puts the
                "Add Skill Manually" button on the same horizontal plane as the first
                skill card. */}
            <div className={styles.panelHeader}>
              <h2 className={styles.panelTitle}>Skill Proposals</h2>
            </div>

            <div className={styles.candidateFiltersWrap}>
              <div className={styles.candidateFilters} role="tablist" aria-label="Filter skill proposals">
                {CANDIDATE_FILTERS.map(filter => (
                  <button
                    key={filter.status}
                    type="button"
                    role="tab"
                    className={`${styles.candidateFilter} ${candidateStatus === filter.status ? styles.candidateFilterActive : ''}`}
                    aria-selected={candidateStatus === filter.status}
                    onClick={() => setCandidateStatus(filter.status)}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Sits between the panel label and the first proposed card; top-aligned
                with the first skill card. The wrapper reserves the same scrollbar
                gutter the card list does, so the button's right edge lines up with the
                cards (not the scrollbar). */}
            <div className={styles.addSkillWrap}>
              <button
                type="button"
                className={styles.addSkillBtn}
                onClick={openAddSkill}
              >
                + Add Skill Manually
              </button>
            </div>

            <div className={styles.approveList}>
              {loading.approve
                ? Array(2).fill(0).map((_, i) => <div key={i} className={styles.approveSkeleton} />)
                : visibleCandidates.length === 0
                  ? <div className={styles.emptyState}>No {candidateStatus === 'normalized' ? 'candidate' : candidateStatus} skills.</div>
                  : visibleCandidates.map(skill => (
                      <NewSkillCard
                        key={skill.id}
                        skillId={skill.id}
                        skillName={skill.skill_name}
                        description={skill.description}
                        status={skill.status ?? 'proposed'}
                        tickets={skill.tickets}
                        rejectedReason={skill.rejected_reason}
                        reviewedBy={skill.reviewed_by}
                        reviewedAt={skill.reviewed_at}
                        onMerge={() => { setMergeError(''); setMergeSkill(skill) }}
                        onApprove={() => openApprove(skill)}
                        onReject={() => setDecision({ mode: 'reject', skill })}
                      />
                    ))
              }
            </div>
          </div>
        )}

      </div>

      {decision && (
        <ProposedSkillDialog
          mode={decision.mode}
          skillName={decision.skill.skill_name}
          types={skillTypes}
          busy={busy}
          onConfirm={payload => handleDecision(decision.skill, decision.mode, payload)}
          onCancel={() => setDecision(null)}
        />
      )}

      {addOpen && (
        <AddSkillModal
          types={skillTypes}
          busy={addBusy}
          error={addError}
          onSave={handleAddSkill}
          onCancel={() => setAddOpen(false)}
        />
      )}

      {duplicate && (
        <DuplicateSkillModal
          candidateName={duplicate.candidate.skill_name}
          existing={duplicate.existing}
          busy={duplicateBusy}
          error={duplicateError}
          onMerge={handleDuplicateMerge}
          onCancel={() => setDuplicate(null)}
        />
      )}

      {mergeSkill && (
        <MergeSkillModal
          skillName={mergeSkill.skill_name}
          skills={skills}
          busy={mergeBusy}
          error={mergeError}
          onMerge={handleMerge}
          onCancel={() => setMergeSkill(null)}
        />
      )}

      {detailSkill && (
        <SkillDetailsModal
          key={detailSkill.id}
          skillId={detailSkill.id}
          skillName={detailSkill.name}
          skillType={detailSkill.type}
          skillDescription={detailSkill.description}
          skillTarget={detailSkill.target_agent_count}
          types={skillTypes}
          onClose={() => setDetailSkill(null)}
          onSkillMutated={refreshSkill}
          onSkillUpdated={applySkillUpdate}
          onSkillDeleted={id => setSkills(list => list.filter(s => s.id !== id))}
        />
      )}
    </div>
  )
}
